import { useCallback } from 'react';
import dayjs from 'dayjs';

export const useBarChartProcessor = () => {
  return useCallback((response) => {
    if (!response || response.length === 0) return [];

    // First, find all unique countries and their total cars
    const countryTotals = {};
    for (const item of response) {
      const code = item.countryCode;
      const name = item.countryName; // assume consistent per code
      if (!countryTotals[code]) {
        countryTotals[code] = { name, totalCars: 0 };
      }
      countryTotals[code].totalCars += item.effectiveShippedCount;
    }

    // Get top 3 countries by totalCars
    const sortedCountries = Object.entries(countryTotals).sort(
      (a, b) => b[1].totalCars - a[1].totalCars
    );
    const top3 = sortedCountries
      .slice(0, 3)
      .map(([code, { name }]) => ({ code, name }));
    const topCodes = new Set(top3.map((c) => c.code));
    const hasRest = sortedCountries.length > 3;

    // Now, build dataset
    const dataset = {};
    for (const item of response) {
      const monthYear = `${dayjs()
        .month(item.month - 1)
        .format('MMM')} ${item.year}`;
      if (!dataset[monthYear]) {
        dataset[monthYear] = { month: monthYear };
        // Initialize top3 to 0
        for (const { name } of top3) {
          dataset[monthYear][`${name}_Cars`] = 0;
          dataset[monthYear][`${name}_Ships`] = 0;
        }
        if (hasRest) {
          dataset[monthYear]['Rest_Cars'] = 0;
          dataset[monthYear]['Rest_Ships'] = 0;
        }
      }

      const code = item.countryCode;
      if (topCodes.has(code)) {
        const name = item.countryName;
        dataset[monthYear][`${name}_Cars`] += item.effectiveShippedCount;
        dataset[monthYear][`${name}_Ships`] += item.totalEntries;
      } else if (hasRest) {
        dataset[monthYear]['Rest_Cars'] += item.effectiveShippedCount;
        dataset[monthYear]['Rest_Ships'] += item.totalEntries;
      }
    }

    return Object.values(dataset);
  }, []);
};

export const usePortBarChartProcessor = () => {
  return useCallback((response) => {
    if (!response || !Array.isArray(response) || response.length === 0) {
      return [];
    }

    // Step 1: Compute total cars per country to determine top 3
    const countryTotals = {};
    response.forEach((item) => {
      const { countryCode, countryName, effectiveShippedCount } = item;
      if (!countryTotals[countryCode]) {
        countryTotals[countryCode] = { name: countryName, totalCars: 0 };
      }
      countryTotals[countryCode].totalCars += effectiveShippedCount || 0;
    });

    // Sort countries by totalCars descending
    const sortedCountries = Object.entries(countryTotals).sort(
      ([, a], [, b]) => b.totalCars - a.totalCars
    );
    const top2 = sortedCountries
      .slice(0, 2)
      .map(([code, { name }]) => ({ code, name }));
    const topCodes = new Set(top2.map((c) => c.code));
    const hasRest = sortedCountries.length > 2;

    // Step 2: Collect unique port keys for top 3 countries (using unLocode for uniqueness)
    const topPortKeys = new Set();
    response.forEach((item) => {
      if (topCodes.has(item.countryCode)) {
        const portKey = `${item.unLocode} (${item.countryCode})`;
        topPortKeys.add(portKey);
      }
    });

    // Step 3: Build dataset by month
    const dataset = {};
    response.forEach((item) => {
      const {
        year,
        month: itemMonth,
        countryCode,
        unLocode,
        effectiveShippedCount,
        totalEntries,
      } = item;
      const monthYear = `${dayjs()
        .month(itemMonth - 1)
        .format('MMM')} ${year}`;
      if (!dataset[monthYear]) {
        dataset[monthYear] = { month: monthYear };
        // Initialize top port keys to 0
        topPortKeys.forEach((portKey) => {
          dataset[monthYear][`${portKey}_Cars`] = 0;
          dataset[monthYear][`${portKey}_Ships`] = 0;
        });
        // Initialize Rest if applicable
        if (hasRest) {
          dataset[monthYear]['Rest_Cars'] = 0;
          dataset[monthYear]['Rest_Ships'] = 0;
        }
      }

      const portKey = `${unLocode} (${countryCode})`;
      if (topCodes.has(countryCode)) {
        dataset[monthYear][`${portKey}_Cars`] += effectiveShippedCount || 0;
        dataset[monthYear][`${portKey}_Ships`] += totalEntries || 0;
      } else if (hasRest) {
        dataset[monthYear]['Rest_Cars'] += effectiveShippedCount || 0;
        dataset[monthYear]['Rest_Ships'] += totalEntries || 0;
      }
    });

    // Step 4: Return sorted by month (assuming chronological order needed)
    return Object.values(dataset).sort((a, b) => {
      const dateA = dayjs(a.month, 'MMM YYYY');
      const dateB = dayjs(b.month, 'MMM YYYY');
      return dateA.isBefore(dateB) ? -1 : 1;
    });
  }, []);
};

export const useVesselScheduleTransformer = (formatDate) => {
  return useCallback(
    (item) => {
      const departureDelay = item.departureDelay;
      let status = 'OnTime';
      if (departureDelay > 7) status = 'MajorDelay';
      else if (departureDelay >= 3) status = 'MinorDelay';

      return {
        ...item,
        departurePortName: item.departurePort?.name,
        departureCountryName: item.departureCountry?.name,
        destinationPortName: item.destinationPort?.name,
        destinationCountryName: item.destinationCountry?.name,
        shippingCompanyName: item.shippingCompany?.supplierName,
        portOperatorName: item.portOperator?.supplierName,
        initialETDFormatted: formatDate(item.initialETD),
        initialETAFormatted: formatDate(item.initialETA),
        actualETDFormatted: formatDate(item.actualETD),
        actualETAFormatted: formatDate(item.actualETA),
        status,
      };
    },
    [formatDate]
  );
};

export const useTableDataProcessor = () => {
  return useCallback((data, periodFormatter, setTableData) => {
    const rows = [
      { id: 1, rowName: 'Voyages Departed' },
      { id: 2, rowName: 'Cars Departed' },
      { id: 3, rowName: 'Voyages Arrived' },
      { id: 4, rowName: 'Cars Arrived' },
    ];

    const columns = [{ field: 'rowName', headerName: 'Row Names', width: 150 }];

    const processResponse = (response, type) => {
      response?.forEach((item) => {
        const period = periodFormatter(item);
        if (!columns.some((c) => c.field === period)) {
          columns.push({ field: period, headerName: period, width: 150 });
        }

        const rowIndex = type === 'ETD' ? 0 : 2;
        rows[rowIndex][period] = item.totalEntries;
        rows[rowIndex + 1][period] = item.totalEffectiveShipped;
      });
    };

    processResponse(data.ETD, 'ETD');
    processResponse(data.ETA, 'ETA');
    setTableData({ columns, rows });
  }, []);
};
